import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Copy, Check, MessageCircle, Clock, Target, Zap, AlertTriangle, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

// Theme
const theme = {
  bg: '#f5f3f0',
  card: '#ffffff',
  border: '#e0dbd4',
  text: '#1a1a1a',
  textMuted: 'rgba(26,26,26,0.6)',
  orange: '#F05A1A',
  green: '#16a34a',
  yellow: '#d97706',
};

// Access code for Setter pages
const SETTER_CODE = 'setter5';

// Lock Screen Component
const LockScreen = ({ onUnlock, title, color }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.toLowerCase() === SETTER_CODE) {
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.bg }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${color}20` }}>
          <Lock className="w-8 h-8" style={{ color }} />
        </div>
        <h1 className="font-bebas text-3xl text-gray-800 mb-2">{title}</h1>
        <p className="text-gray-500 mb-6">Entrez le code d'accès Setter pour continuer</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code d'accès"
            className={`w-full px-4 py-3 rounded-xl border-2 text-center font-bold tracking-widest uppercase outline-none transition-colors ${
              error ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-orange-500'
            }`}
            data-testid="setter-guide-code-input"
          />
          {error && <p className="text-red-500 text-sm mt-2">Code incorrect</p>}
          <button
            type="submit"
            className="w-full mt-4 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: color }}
            data-testid="setter-guide-unlock-btn"
          >
            DÉVERROUILLER
          </button>
        </form>
        
        <Link 
          to="/recrutement?tab=setter" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm mt-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au recrutement
        </Link>
      </motion.div>
    </div>
  );
};

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
      style={{ 
        backgroundColor: copied ? 'rgba(22,163,74,0.1)' : 'rgba(240,90,26,0.1)',
        color: copied ? theme.green : theme.orange,
        border: `1px solid ${copied ? 'rgba(22,163,74,0.3)' : 'rgba(240,90,26,0.3)'}`
      }}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copié !' : 'Copier'}
    </motion.button>
  );
};

// Message template component
const MessageTemplate = ({ title, badge, badgeColor = 'orange', content, note }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-4 h-4" style={{ color: theme.orange }} />
        <span className="font-bold text-gray-800">{title}</span>
        {badge && (
          <span 
            className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
            style={{ 
              backgroundColor: badgeColor === 'green' ? 'rgba(22,163,74,0.1)' : badgeColor === 'yellow' ? 'rgba(217,119,6,0.1)' : 'rgba(240,90,26,0.1)',
              color: badgeColor === 'green' ? theme.green : badgeColor === 'yellow' ? theme.yellow : theme.orange
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <CopyButton text={content} />
    </div>
    <div 
      className="text-sm leading-relaxed p-4 rounded-lg whitespace-pre-line"
      style={{ backgroundColor: '#fafaf8', color: theme.text }}
    >
      {content}
    </div>
    {note && (
      <p className="text-xs mt-3 italic" style={{ color: theme.textMuted }}>
        💡 {note}
      </p>
    )}
  </div>
);

// Expandable section
const Section = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <span className="font-bold text-gray-800">{title}</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
          <ChevronDown className="w-5 h-5" style={{ color: theme.orange }} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Rule card
const RuleCard = ({ icon, title, description }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="font-bold text-gray-800 text-sm">{title}</p>
      <p className="text-xs text-gray-600 mt-0.5">{description}</p>
    </div>
  </div>
);

const SetterGuidePage = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);

  if (!isUnlocked) {
    return <LockScreen onUnlock={() => setIsUnlocked(true)} title="GUIDE DM SETTER" color={theme.orange} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/recrutement?tab=setter" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la page Setter
          </Link>
          <h1 className="text-white font-bebas text-4xl tracking-wide">
            GUIDE DM SETTER
          </h1>
          <p className="text-white/80 text-sm mt-2">
            Séquence complète Instagram DM · Scripts prêts à copier
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        
        {/* Objectif */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 border-2 border-orange-300"
        >
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6" style={{ color: theme.orange }} />
            <h2 className="font-bebas text-2xl" style={{ color: theme.orange }}>OBJECTIF</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            Ton seul objectif dans ces messages : <strong>obtenir une réponse</strong>. Pas pitcher. Pas convaincre. Juste ouvrir la conversation.
          </p>
          <p className="text-gray-600 mt-2">
            Une fois qu'ils répondent, tu qualifies. Une fois qualifiés, tu envoies le Calendly.
          </p>
        </motion.div>

        {/* Étape 1 - Commentaire */}
        <Section title="ÉTAPE 1 — Réponse au commentaire" icon="💬" defaultOpen>
          <p className="text-sm text-gray-600 mb-4">
            Dès qu'un abonné commente « DIAGNOSTIC » sous le Reel, tu réponds <strong>immédiatement</strong> sous le commentaire :
          </p>
          <MessageTemplate
            title="Réponse commentaire"
            badge="< 5 min"
            badgeColor="yellow"
            content={`✅ C'est parti [Prénom] — check tes DM, c'est sûrement dans tes demandes masquées 🤝`}
            note="Répondre dans les 5 minutes. Après, le lead refroidit."
          />
        </Section>

        {/* Étape 2 - DM Initial */}
        <Section title="ÉTAPE 2 — DM d'envoi du diagnostic" icon="📩" defaultOpen>
          <p className="text-sm text-gray-600 mb-4">
            Dans la foulée, tu envoies ce DM. <strong>Deux options :</strong>
          </p>
          
          {/* Option courte */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">OPTION 1 — Court & Direct</span>
              <span className="text-xs text-gray-500">Recommandé</span>
            </div>
            <MessageTemplate
              title="DM Court"
              badge="Le plus efficace"
              badgeColor="green"
              content={`Salut, 👉 audit-resto.fr (2 min)

Envoie-moi ton score à la fin 🤝📈
Tu me diras si ça reflète ce que tu penses de ton resto.`}
            />
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>💡 Pourquoi ça marche :</strong> Le "envoie-moi ton score" c'est un <strong>engagement public</strong> — ils se sentent obligés de finir et de répondre. C'est exactement ce qu'il faut.
              </p>
            </div>
          </div>
          
          {/* Option longue */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-gray-100 text-gray-700 text-xs font-bold px-2 py-1 rounded-full">OPTION 2 — Version détaillée</span>
            </div>
            <MessageTemplate
              title="DM Complet"
              badge="Alternative"
              content={`Salut 👋

Comme promis — voici ton diagnostic gratuit.

J'ai analysé +100 restaurants ces derniers mois. Presque tous perdaient entre 8 et 15% de leur CA sans le savoir. Food cost mal calibré, masse salariale qui dérape, livraison qui bouffe les marges — souvent les 4 en même temps.

8 questions, moins de 2 minutes.
À la fin tu sais exactement où ça fuit chez toi.

👉 audit-resto.fr

L'ebook avec les actions concrètes arrive automatiquement une fois terminé 🤝`}
            />
          </div>
        </Section>

        {/* Étape 3 - Relances */}
        <Section title="ÉTAPE 3 — Relances (si pas de réponse)" icon="🔄">
          <div className="space-y-4">
            <MessageTemplate
              title="Relance J+1"
              badge="Pas rempli"
              badgeColor="yellow"
              content={`[Prénom] t'as pas eu le temps de finir ? 😄`}
            />
            
            <MessageTemplate
              title="Relance J+3"
              badge="Toujours rien"
              badgeColor="yellow"
              content={`[Prénom].

Tu es passé sur le diagnostic. Tu n'as pas fini.

Je sais pas ce qui t'a arrêté — le manque de temps, une distraction, ou peut-être que tu te dis que tu connais déjà tes problèmes.

Mais les restaurateurs qui pensent "je sais où ça bloque" sont souvent ceux qui passent à côté de la vraie fuite. Pas le food cost. Pas les charges. Quelque chose de plus discret.

2 minutes. 8 questions.

👉 audit-resto.fr`}
            />
            
            <MessageTemplate
              title="Relance J+7"
              badge="Dernière tentative"
              badgeColor="orange"
              content={`[Prénom] je te relance une dernière fois.

Si c'est pas le bon moment, pas de souci — dis-moi et je te contacte plus.

Mais si ton resto te prend plus qu'il te rapporte, on peut changer ça.

👉 audit-resto.fr`}
            />
          </div>
        </Section>

        {/* Étape 4 - Qualification */}
        <Section title="ÉTAPE 4 — Qualification (après diagnostic)" icon="🎯">
          <p className="text-sm text-gray-600 mb-4">
            Une fois qu'il a rempli le diagnostic, tu envoies les messages de qualification <strong>un par un</strong>. Tu attends la réponse avant d'envoyer le suivant.
          </p>
          
          <div className="space-y-4">
            <MessageTemplate
              title="Message 1"
              badge="Ouvrir"
              badgeColor="green"
              content={`Salut [Prénom], j'ai bien reçu ton diagnostic.

Dis-moi — tu l'as fait par curiosité ou tu cherches vraiment à améliorer ton restaurant en ce moment ?`}
            />
            
            <MessageTemplate
              title="Message 2"
              badge="Déclencheur"
              content={`Qu'est-ce qui t'a donné envie de le faire ?`}
            />
            
            <MessageTemplate
              title="Message 3"
              badge="Urgence"
              content={`Et si ton restaurant continue exactement comme aujourd'hui pendant 12 mois — ça te va ou ça doit changer ?`}
              note="S'il dit « ça doit changer », tu continues."
            />
            
            <MessageTemplate
              title="Message 4"
              badge="Problème"
              content={`Ok je vois. Aujourd'hui c'est quoi le plus gros blocage dans ton restaurant ?`}
            />
            
            <MessageTemplate
              title="Message 5"
              badge="Douleur"
              content={`Et ça t'impacte comment au quotidien ?`}
            />
            
            <MessageTemplate
              title="Message 6"
              badge="Disposition"
              content={`T'es plutôt en mode "je cherche des idées" ou tu veux vraiment débloquer la situation ?`}
              note="S'il dit « débloquer », tu passes à la transition."
            />
            
            <MessageTemplate
              title="Message 7"
              badge="Transition"
              badgeColor="green"
              content={`Ok je vois mieux ta situation.

J'ai justement créé un accompagnement sur 90 jours pour les restaurateurs où on travaille exactement sur ces points.

Si tu veux, on fait un échange rapide pour voir si c'est pertinent pour toi 👉 [lien Calendly]`}
            />
          </div>
        </Section>

        {/* Étape 5 - Relance call */}
        <Section title="ÉTAPE 5 — Relance call (J+1)" icon="📅">
          <MessageTemplate
            title="Relance booking"
            badge="J+1"
            content={`Salut [Prénom], comme vu ensemble hier — tu me confirmes aujourd'hui ?

Je te pose la question simplement pour savoir si je bloque la place pour toi ou si je l'ouvre à quelqu'un d'autre.`}
          />
        </Section>

        {/* Exemples de réponses */}
        <Section title="Exemples de réponses & comment réagir" icon="💬">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il dit « juste par curiosité »</p>
              <p className="text-gray-600 text-sm mb-2">Ne lâche pas. Curiosité = il a quand même un problème. Creuse :</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « Ah intéressant. Et t'as trouvé des résultats surprenants ? »
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il dit « ça tourne bien mais je veux toujours m'améliorer »</p>
              <p className="text-gray-600 text-sm mb-2">C'est les meilleurs profils ça :</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « Concrètement c'est quoi le truc que tu veux améliorer en priorité ? »
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Le blocage c'est les marges</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « Les marges oui. C'est plutôt côté food cost ou côté masse salariale que ça pèse le plus ? »
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Le blocage c'est le staff</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « Le staff c'est souvent le plus chronophage. C'est plutôt le recrutement, la fidélisation ou l'organisation du planning ? »
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il dit « je cherche juste des idées »</p>
              <p className="text-gray-600 text-sm mb-2">Il n'est pas chaud. Ne pitcher pas encore :</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « Ok je vois. Je t'envoie une ressource sur ce point — ça peut déjà t'aider à voir les leviers. »
              </div>
              <p className="text-xs text-gray-500 mt-2">Puis relance dans 3-4 jours.</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il dit « c'est trop cher » (avant même d'avoir vu le prix)</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « On n'a pas encore parlé de budget — le call c'est juste pour voir si c'est pertinent pour ta situation. C'est 30 min, sans engagement. »
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il dit « je vais réfléchir »</p>
              <div className="bg-white rounded p-3 text-sm italic text-gray-700">
                « Pas de souci. Tu réfléchis à quoi exactement ? Je peux peut-être t'aider à trancher. »
              </div>
            </div>
          </div>
        </Section>

        {/* Règles d'or */}
        <Section title="Règles d'or" icon="⚡" defaultOpen>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RuleCard 
              icon="🕐" 
              title="Réactivité" 
              description="Répondre au commentaire dans les 5 min. Le lead refroidit vite." 
            />
            <RuleCard 
              icon="💬" 
              title="Un à la fois" 
              description="1 message à la fois. Jamais 2 messages sans réponse entre les deux." 
            />
            <RuleCard 
              icon="🚫" 
              title="Pas de pitch" 
              description="Ne jamais pitcher dans les relances. L'objectif c'est juste d'obtenir une réponse." 
            />
            <RuleCard 
              icon="❓" 
              title="Toujours une question" 
              description="Toujours terminer par une question. Une question = une réponse possible." 
            />
            <RuleCard 
              icon="👍" 
              title="Respect du non" 
              description="Si 'pas intéressé' → 'Pas de souci 👍'. Ne pas insister." 
            />
            <RuleCard 
              icon="⏱️" 
              title="Timing relances" 
              description="Attendre 24-48h entre chaque relance. Ne pas spammer." 
            />
          </div>
          
          <div className="mt-6 p-4 rounded-xl bg-green-50 border-2 border-green-300">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-green-600" />
              <span className="font-bold text-green-700">Objectif final</span>
            </div>
            <p className="text-green-700">
              <strong>Obtenir un call booké sur Calendly.</strong> Rien d'autre.
            </p>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center pt-8 pb-12">
          <Link 
            to="/recrutement?tab=setter"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la page Setter
          </Link>
          <p className="text-gray-400 text-xs mt-6">
            © 2025 PRSNL · Guide Setter v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetterGuidePage;
